// Web (CSS/flexbox) style objects for the page-builder containers. The leaf
// blocks (text/image/button/line/spacer) reuse the newsletter style functions
// directly — see PageRenderer — so only the web-only containers (section, row,
// column, hero) need their own styles here. No tables, no email hacks.
import type { CSSProperties } from 'react';
import type { PageTheme, SectionBlock, RowBlock, ColumnBlock, HeroBlock } from '../../../shared/pageBlocks';
import type { NewsletterTheme } from '../../../shared/newsletterBlocks';
import { fontStack } from '../../../shared/newsletterBlocks';

// The reused newsletter leaf style fns expect a NewsletterTheme; PageTheme is a
// superset of the fields they read. One mapping, shared by renderer + canvas.
export function asNewsletterTheme(theme: PageTheme): NewsletterTheme {
  return {
    emailBgColor: theme.pageBgColor,
    contentBgColor: theme.contentBgColor,
    fontPackId: theme.fontPackId,
    baseFontSize: theme.baseFontSize,
    linkColor: theme.linkColor,
  };
}

export function pageRootStyle(theme: PageTheme): CSSProperties {
  return {
    backgroundColor: theme.pageBgColor,
    color: theme.textColor,
    fontFamily: fontStack(theme.fontPackId),
    fontSize: theme.baseFontSize,
    fontWeight: theme.baseFontWeight ?? 400,
    fontStyle: theme.baseItalic ? 'italic' : 'normal',
    minHeight: '100dvh',
    width: '100%',
  };
}

// The centered shell that caps the WHOLE site (top bar + content + footer) at
// the site-wide theme.contentMaxWidth and centers it, so on ultra-wide screens
// everything — including backgrounds and the nav — stops at the max width and
// the page background shows on either side (like sleepycircuits.com). One place
// owns the cap; sections never set their own width.
export function siteShellStyle(theme: PageTheme | null | undefined): CSSProperties {
  return {
    maxWidth: theme?.contentMaxWidth ?? 1200,
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    // Set the site font + default weight/italic on the shell so the nav bar
    // inherits them too (the nav lives here, outside pageRootStyle which only
    // fonts the page content).
    fontFamily: fontStack(theme?.fontPackId),
    fontWeight: theme?.baseFontWeight ?? 400,
    fontStyle: theme?.baseItalic ? 'italic' : 'normal',
  };
}

// A section band: fills the shell width with its background, and insets its
// content by the SITE-WIDE horizontal gutter (theme.contentPaddingX, clamped on
// mobile). Vertical padding stays per-section. No per-section width/full-bleed —
// the shell owns the width.
export function sectionOuterStyle(section: SectionBlock, theme: PageTheme, isMobile = false): CSSProperties {
  const gutter = theme.contentPaddingX ?? 24;
  const padX = isMobile ? Math.min(gutter, 16) : gutter;
  const padY = isMobile ? Math.min(section.paddingY, 48) : section.paddingY;
  const style: CSSProperties = {
    width: '100%',
    paddingTop: padY,
    paddingBottom: padY,
    paddingLeft: padX,
    paddingRight: padX,
    boxSizing: 'border-box',
    // Contain any too-wide child (notably a <video>, whose intrinsic width can
    // exceed a narrow mobile column) within the section, so it can never push the
    // whole page wider than the viewport and shift the centered layout. `clip`
    // (not hidden) doesn't create a scroll container, so the desktop sticky buy
    // box still works.
    overflowX: 'clip',
  };
  if (section.bgColor && section.bgColor !== 'transparent') style.backgroundColor = section.bgColor;
  if (section.bgImage) {
    style.backgroundImage = `url(${section.bgImage})`;
    style.backgroundSize = 'cover';
    style.backgroundPosition = 'center';
  }
  return style;
}

// Inner content wrapper — width is capped site-wide by siteShellStyle, so this
// just fills the section's content area.
export function sectionInnerStyle(): CSSProperties {
  return { width: '100%' };
}

export function rowStyle(row: RowBlock, isMobile = false): CSSProperties {
  const alignMap: Record<RowBlock['align'], CSSProperties['alignItems']> = {
    top: 'flex-start',
    center: 'center',
    stretch: 'stretch',
  };
  // On mobile, honor the "Stack on mobile" toggle: columns go full-width vertical.
  const stack = isMobile && row.stackOnMobile;
  return {
    display: 'flex',
    flexDirection: stack ? 'column' : 'row',
    flexWrap: 'wrap',
    gap: isMobile ? Math.min(row.gap, 16) : row.gap,
    alignItems: stack ? 'stretch' : (alignMap[row.align] || 'flex-start'),
    width: '100%',
  };
}

export function columnStyle(column: ColumnBlock, isMobile = false): CSSProperties {
  if (isMobile) {
    // On mobile the row stacks via `flex-direction: column` (see rowStyle), so the
    // MAIN axis is vertical. A `flex-basis: 100%` here would mean 100% HEIGHT —
    // making every stacked column full-height and PILE ON TOP OF EACH OTHER (it
    // bit videos hard: their height is indefinite while loading, so the column
    // collapsed and the next overlapped it). Instead: full WIDTH + basis auto so
    // each column is its content's height and they stack cleanly.
    return { width: '100%', flexBasis: 'auto', flexGrow: 0, flexShrink: 0, minWidth: 0 };
  }
  // Desktop: flex-grow by the column's fraction; basis 0 so columns share
  // proportionally; minWidth forces wrapping on narrower viewports.
  return {
    flexGrow: column.widthFraction || 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 240,
  };
}

export function heroOuterStyle(hero: HeroBlock): CSSProperties {
  const style: CSSProperties = {
    position: 'relative',
    minHeight: hero.minHeight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: hero.align === 'left' ? 'flex-start' : hero.align === 'right' ? 'flex-end' : 'center',
    overflow: 'hidden',
    width: '100%',
  };
  if (hero.bgImage) {
    style.backgroundImage = `url(${hero.bgImage})`;
    style.backgroundSize = 'cover';
    style.backgroundPosition = 'center';
  } else {
    style.backgroundColor = '#18181b';
  }
  return style;
}

export function heroOverlayStyle(hero: HeroBlock): CSSProperties {
  return {
    position: 'absolute',
    inset: 0,
    backgroundColor: hero.overlayColor,
    opacity: hero.bgImage ? hero.overlayOpacity : 0,
    pointerEvents: 'none',
  };
}

export function heroContentStyle(hero: HeroBlock): CSSProperties {
  return {
    position: 'relative',
    zIndex: 1,
    textAlign: hero.align,
    color: hero.textColor,
    padding: '32px 24px',
    maxWidth: 800,
  };
}
